import {
  COMMERCE_DELIVERY_RADIUS_KM_MIN,
  COMMERCE_DELIVERY_RADIUS_UI_KM_MAX,
  COMMERCE_LISTED_ZONES_ID_MAX,
  COMMERCE_LISTED_ZONES_MAX_JURISDICTIONS,
  type CommerceDeliveryScope,
} from '../../../../services/commerceService.ts';
import { buildListedZonesByJurisdictionBuckets } from './commerceFormListedBuckets.ts';

export type CommerceFormFieldValidation = {
  ok: boolean;
  issues: string[];
  firstFocusId: string | null;
  invalidFieldIds: string[];
};

export function scrollCommerceFormFieldIntoView(elementId: string) {
  requestAnimationFrame(() => {
    const el = document.getElementById(elementId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (el instanceof HTMLElement && typeof el.focus === 'function') {
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
    }
  });
}

export function computeCommerceFormValidation(params: {
  logoFile: File | null;
  prefillLogoUrl?: string | null;
  name: string;
  description: string;
  deliveryScope: CommerceDeliveryScope;
  listedZonesIds: string[];
  zone: string;
  jurisdiccionId: number | '' | null;
  deliveryRadiusKm: number;
  idToListedJurisdiction: Record<string, number>;
}): CommerceFormFieldValidation {
  const {
    logoFile,
    prefillLogoUrl,
    name,
    description,
    deliveryScope,
    listedZonesIds,
    zone,
    jurisdiccionId,
    deliveryRadiusKm,
    idToListedJurisdiction,
  } = params;

  const issues: string[] = [];
  const invalidFieldIds: string[] = [];
  let firstFocusId: string | null = null;
  const addIssue = (msg: string, focusId: string) => {
    issues.push(msg);
    if (firstFocusId === null) firstFocusId = focusId;
    if (!invalidFieldIds.includes(focusId)) invalidFieldIds.push(focusId);
  };

  const hasLogo = logoFile != null || Boolean(prefillLogoUrl?.trim());
  if (!hasLogo) {
    addIssue('Subí el logo del comercio (JPG o PNG).', 'commerce-logo-pick');
  }
  if (!name.trim()) {
    addIssue('Completá el nombre del comercio.', 'commerce-name');
  }
  if (!description.trim()) {
    addIssue('Completá la descripción.', 'commerce-desc');
  }
  if (deliveryScope === 'LISTED_ZONES') {
    const jurOk = jurisdiccionId !== '' && jurisdiccionId !== null;
    if (!jurOk) {
      addIssue('Seleccioná la provincia del listado para ver y elegir zonas.', 'commerce-jur');
    } else if (listedZonesIds.length < 1) {
      addIssue('Elegí al menos una zona del listado.', 'commerce-listed-zones-search');
    }
  }
  if (jurisdiccionId === '' || jurisdiccionId === null) {
    if (deliveryScope !== 'LISTED_ZONES') {
      addIssue(
        'Seleccioná la provincia o elegí una dirección que permita detectarla.',
        deliveryScope === 'LOCAL' ? 'commerce-place-local' : 'commerce-delivery-focus'
      );
    }
  }
  if (!zone.trim()) {
    if (!(deliveryScope === 'LISTED_ZONES' && listedZonesIds.length < 1)) {
      addIssue(
        deliveryScope === 'LOCAL'
          ? 'Elegí un punto en el mapa o una dirección en el buscador para definir la cobertura.'
          : 'Completá la cobertura de tu comercio.',
        deliveryScope === 'LOCAL' ? 'commerce-place-local' : 'commerce-delivery-focus'
      );
    }
  }
  if (deliveryScope === 'LOCAL') {
    if (
      deliveryRadiusKm < COMMERCE_DELIVERY_RADIUS_KM_MIN ||
      deliveryRadiusKm > COMMERCE_DELIVERY_RADIUS_UI_KM_MAX ||
      !Number.isFinite(deliveryRadiusKm)
    ) {
      addIssue(
        `Indicá un alcance de zona entre ${COMMERCE_DELIVERY_RADIUS_KM_MIN} y ${COMMERCE_DELIVERY_RADIUS_UI_KM_MAX} km.`,
        'commerce-radius-km-num'
      );
    }
  }
  if (deliveryScope === 'LISTED_ZONES' && jurisdiccionId !== '' && jurisdiccionId !== null) {
    const fj = Number(jurisdiccionId);
    const bucketDtos = buildListedZonesByJurisdictionBuckets(
      listedZonesIds,
      idToListedJurisdiction,
      fj
    ).filter((b) => b.georefZonaIds.length > 0);
    const n = bucketDtos.reduce((s, b) => s + b.georefZonaIds.length, 0);
    if (listedZonesIds.length >= 1 && n < 1) {
      addIssue(
        'Revisá las zonas seleccionadas: falta asociar provincia o hubo un problema con la selección.',
        'commerce-listed-zones-search'
      );
    }
    if (n > COMMERCE_LISTED_ZONES_ID_MAX) {
      addIssue(`Podés elegir como máximo ${COMMERCE_LISTED_ZONES_ID_MAX} zonas en total.`, 'commerce-listed-zones-search');
    }
    if (bucketDtos.length > COMMERCE_LISTED_ZONES_MAX_JURISDICTIONS) {
      addIssue(
        `Podés combinar hasta ${COMMERCE_LISTED_ZONES_MAX_JURISDICTIONS} provincias en el listado.`,
        'commerce-listed-zones-search'
      );
    }
  }

  return { ok: issues.length === 0, issues, firstFocusId, invalidFieldIds };
}
